import React, { useCallback, useEffect, useState } from 'react';
import {
  BudgetRequest,
  BudgetProposal,
  BudgetCounterProposalCreateInput,
} from '@models/budget.model';
import { useBudget } from '@hooks/api/useBudget';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import CounterProposalModal from '@components/CounterProposalModal';
import BudgetResponseModal from '@components/organisms/BudgetResponseModal';
import {
  ProposalHistorySlider,
} from './components';

interface BudgetDetailsModalProps {
  open: boolean;
  onClose: () => void;
  budgetRequest: BudgetRequest;
  onRefresh: () => void;
}

const BudgetDetailsModal: React.FC<BudgetDetailsModalProps> = ({
  open,
  onClose,
  budgetRequest,
  onRefresh,
}) => {
  const [proposals, setProposals] = useState<BudgetProposal[]>([]);
  const [counterProposalModalOpen, setCounterProposalModalOpen] =
    useState(false);
  const [selectedProposalForCounter, setSelectedProposalForCounter] =
    useState<BudgetProposal | null>(null);
  const [responseModalOpen, setResponseModalOpen] = useState(false);
  const [proposalHistorySliderOpen, setProposalHistorySliderOpen] =
    useState(false);
  const [selectedOrganizationForHistory, setSelectedOrganizationForHistory] =
    useState<{
      id: string;
      name: string;
    } | null>(null);

  const { organizationId } = useAppSelector(
    (state: RootState) => state.organization,
  );

  const {
    fetchBudgetProposalConversation,
    acceptBudgetProposal,
    rejectBudgetProposal,
    acceptBudgetRequest,
    rejectBudgetRequest,
    createCounterProposal,
    respondToBudgetRequest,
    loading: mutationLoading,
  } = useBudget();

  const loadProposals = useCallback(async () => {
    const result = await fetchBudgetProposalConversation(
      budgetRequest.budgetRequestId,
    );
    // Sort proposals by createdAt in descending order (newest first) to ensure we get the latest status
    const sortedProposals = (result || []).sort(
      (a: BudgetProposal, b: BudgetProposal) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    setProposals(sortedProposals);
  }, [fetchBudgetProposalConversation, budgetRequest.budgetRequestId]);

  useEffect(() => {
    if (open && budgetRequest) {
      loadProposals();
    }
  }, [open, budgetRequest, loadProposals]);

  const handleAcceptProposal = async (proposalId: string) => {
    const result = await acceptBudgetProposal(proposalId);
    if (result) {
      onRefresh();
      onClose();
    }
  };

  const handleRejectProposal = async (proposalId: string) => {
    const result = await rejectBudgetProposal(proposalId);
    if (result) {
      loadProposals();
      onRefresh();
    }
  };

  const handleCounterProposal = (proposal: BudgetProposal) => {
    setSelectedProposalForCounter(proposal);
    setCounterProposalModalOpen(true);
  };

  const handleSubmitCounterProposal = async (
    input: BudgetCounterProposalCreateInput,
  ) => {
    const result = await createCounterProposal(input);
    if (result) {
      setCounterProposalModalOpen(false);
      setSelectedProposalForCounter(null);
      loadProposals();
      onRefresh();
    }
  };

  const handleAcceptBudgetRequest = async () => {
    const result = await acceptBudgetRequest(budgetRequest.budgetRequestId);
    if (result) {
      onRefresh();
      onClose();
    }
  };

  const handleRejectBudgetRequest = async () => {
    const result = await rejectBudgetRequest(budgetRequest.budgetRequestId);
    if (result) {
      onRefresh();
      onClose();
    }
  };

  const handleRespondToBudgetRequest = async (input: any) => {
    const result = await respondToBudgetRequest(input);
    if (result) {
      setResponseModalOpen(false);
      loadProposals();
      onRefresh();
    }
  };

  const handleRecipientClick = (
    recipientOrganizationId: string,
    recipientOrganizationName: string,
  ) => {
    setSelectedOrganizationForHistory({
      id: recipientOrganizationId,
      name: recipientOrganizationName,
    });
    setProposalHistorySliderOpen(true);
  };

  // Get the display status for a recipient (latest proposal status or recipient status)
  const getRecipientDisplayStatus = (recipient: any) => {
    // Get all proposals in chronological order (oldest first) to trace the conversation
    const allProposalsChronological = proposals
      .slice()
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );

    // Find the latest proposal that involves this recipient organization
    let latestRelevantProposal = null;
    let latestStatus = recipient.status; // Default to recipient status

    // Walk through all proposals chronologically to find the latest status for this recipient
    for (const proposal of allProposalsChronological) {
      // Check if this proposal is relevant to this recipient
      if (
        proposal.proposingOrganizationId === recipient.recipientOrganizationId
      ) {
        // This is a proposal FROM the recipient organization
        latestRelevantProposal = proposal;
        latestStatus = proposal.status;
      } else if (proposal.proposingOrganizationId === organizationId) {
        // This is a proposal FROM the originator (us)
        // Check if this is responding to a previous proposal from this recipient
        if (proposal.isCounterProposal && proposal.parentProposalId) {
          const parentProposal = proposals.find(
            (p) => p.budgetProposalId === proposal.parentProposalId,
          );
          if (
            parentProposal &&
            parentProposal.proposingOrganizationId ===
            recipient.recipientOrganizationId
          ) {
            // This counter proposal is responding to this recipient's proposal
            latestRelevantProposal = proposal;
            latestStatus = proposal.status;
          }
        }
      }
    }

    return {
      status: latestStatus,
      isFromProposal: latestRelevantProposal !== null,
      proposal: latestRelevantProposal,
    };
  };

  return (
    <>
      {/* Main Bid Request Details Slider - Unified View */}
      <ProposalHistorySlider
        isOpen={open}
        onClose={onClose}
        proposals={proposals}
        selectedOrganizationId={null}
        budgetRequest={budgetRequest}
        organizationId={organizationId || undefined}
        onAcceptProposal={handleAcceptProposal}
        onRejectProposal={handleRejectProposal}
        onCounterProposal={handleCounterProposal}
        getRecipientDisplayStatus={getRecipientDisplayStatus}
        onRecipientClick={handleRecipientClick}
        showFullView={true}
      />

      {/* Counter Proposal Modal */}
      {selectedProposalForCounter && (
        <CounterProposalModal
          open={counterProposalModalOpen}
          onClose={() => {
            setCounterProposalModalOpen(false);
            setSelectedProposalForCounter(null);
          }}
          onSubmit={handleSubmitCounterProposal}
          originalProposal={selectedProposalForCounter!}
          isSubmitting={mutationLoading}
        />
      )}

      {/* Budget Response Modal */}
      <BudgetResponseModal
        open={responseModalOpen}
        onClose={() => setResponseModalOpen(false)}
        onSubmit={handleRespondToBudgetRequest}
        onAccept={handleAcceptBudgetRequest}
        onReject={handleRejectBudgetRequest}
        budgetRequest={budgetRequest}
        isSubmitting={mutationLoading}
      />

      {/* Proposal History Slider for specific recipient */}
      <ProposalHistorySlider
        isOpen={proposalHistorySliderOpen}
        onClose={() => {
          setProposalHistorySliderOpen(false);
          setSelectedOrganizationForHistory(null);
        }}
        proposals={proposals}
        selectedOrganizationId={selectedOrganizationForHistory?.id || null}
        selectedOrganizationName={selectedOrganizationForHistory?.name}
        budgetRequest={budgetRequest}
        organizationId={organizationId || undefined}
        onAcceptProposal={handleAcceptProposal}
        onRejectProposal={handleRejectProposal}
        onCounterProposal={handleCounterProposal}
        showFullView={false}
      />
    </>
  );
};

export default BudgetDetailsModal;
